import { useDisclosure } from '@chakra-ui/react';
import {
  IAccountRespSchema,
  IBillGroupResp,
  InvoiceCreationEnum,
} from '@monetize/types/app';
import { AccountTabTypes, BillGroupTabTypes } from '@monetize/types/common';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useGetById } from '../../../api/queryUtils';
import ContractModal from '../../../components/Contracts/ContractModal';
import { CustomerPortalConfigPopover } from '../../../components/CustomerPortal/CustomerPortalConfigPopover';
import { InvoiceTransformationDrawer } from '../../../components/InvoiceTransformation/InvoiceTransformationDrawer';
import {
  MBox,
  MCustomLinkTab,
  MFlex,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MTabList,
  MTabs,
  MText,
} from '../../../components/Monetize';
import { MPageHeaderLeftSection } from '../../../components/Monetize/MPageHeaderLeftSection';
import { ROUTES } from '../../../constants';
import { billGroupDetailsTabs } from '../../../constants/accounts';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../constants/billGroups';
import {
  BILL_GROUP_BILLING_SCHEDULE,
  BILL_GROUP_DETAILS,
} from '../../../constants/routes';
import { PastDueBadge } from '../../../components/PastDueBadge';
import { useShowPastDueFields } from '../../../hooks';
import { useACL } from '../../../services/acl/acl';
import { FeatureFlag, useFlags } from '../../../services/launchDarkly';
import { PrivateRoute } from '../../AppPrivateRoute';
import { AccountDetailsBillGroupFilter } from '../AccountDetailsBillGroupFilter';
import ContractListAccount from '../Contracts/ContractListAccount';
import { CreditFormCreateModal } from '../Credits/Credits/CreditFormCreateModal';
import { CreditsList } from '../Credits/CreditsList';
import AccountInvoices from '../Invoices/AccountInvoices';
import { PaymentList } from '../Payments/PaymentList';
import QuoteListAccount from '../Quotes/QuoteListAccount';
import { SubscriptionList } from '../Subscriptions/SubscriptionList';
import { UsagePage } from '../Usage/UsagePage';
import { BillGroupGenerateInvoicePopover } from './components/BillGroupGenerateInvoicePopover';
import { DunningStatusBadge } from './components/DunningStatusBadge';
import { InvoiceTransformationBadge } from './components/InvoiceTransformationBadge';
import { BILL_GROUP_TAB_ROUTES } from './constants';
import { BillGroupBillingScheduleTab } from './tabs/BillGroupBillingScheduleTab';
import { BillGroupDetailsTab } from './tabs/BillGroupDetailsTab/BillGroupDetailsTab';

interface BillGroupPageProps {
  isReadOnly?: boolean;
}
export const BillGroupPage = ({ isReadOnly = false }: BillGroupPageProps) => {
  const navigate = useNavigate();
  const featureFlags = useFlags();
  const { enableInvoiceSplit } = featureFlags;
  const { billGroupId = '' } = useParams();
  const { pathname } = useLocation();
  const { canDo } = useACL();
  const showPastDue = useShowPastDueFields();
  const { data: billGroup, isLoading: isBillGroupLoading } =
    useGetById<IBillGroupResp>('billGroups', billGroupId, {
      enabled: !!billGroupId,
      meta: {
        showErrorToast: true,
        navigateTo: ROUTES.ACCOUNT_LIST,
      },
    });

  const { data: account } = useGetById<IAccountRespSchema>(
    'accounts',
    billGroup?.accountId!,
    {
      enabled: !!billGroup?.accountId,
    },
  );

  const [activeTabIndex, setActiveTabIndex] = useState<number>();
  const [invoiceDelay, setInvoiceDelay] = useState<number>(0);

  const invoiceTransformationDrawerState = useDisclosure();

  // Output configuration is an editing flow, so gate it behind the same
  // permissions as the rest of the bill group form — not just the feature flag.
  const canEditSplitConfig =
    enableInvoiceSplit && !isReadOnly && canDo([['billing', 'create']]);

  useEffect(() => {
    setInvoiceDelay(billGroup?.invoiceDelay || 0);
  }, [billGroup?.invoiceDelay]);

  useLayoutEffect(() => {
    const index = BILL_GROUP_TAB_ROUTES.filter(
      ({ requiresFeatureFlag }) =>
        !(
          requiresFeatureFlag &&
          !featureFlags[requiresFeatureFlag as keyof FeatureFlag]
        ),
    ).findIndex(({ route }) => pathname.includes(route));
    setActiveTabIndex(index);
  }, [pathname, featureFlags]);

  const accountTabName = useMemo(() => {
    const [currentPath] = pathname.split('/').reverse();
    return !currentPath ||
      [BILL_GROUP_DETAILS, BILL_GROUP_BILLING_SCHEDULE].includes(currentPath)
      ? 'bill-groups'
      : (currentPath as AccountTabTypes);
  }, [pathname]);

  const billGroupTabName = useMemo(() => {
    const [currentPath] = pathname.split('/').reverse();
    return !currentPath || billGroupDetailsTabs.has(currentPath)
      ? BILL_GROUP_DETAILS
      : (currentPath as BillGroupTabTypes);
  }, [pathname]);

  if (!billGroup || !account || isBillGroupLoading) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer alignItems="stretch" enableAccountSearch>
      <MPageHeader
        renderLeftSection={
          <MPageHeaderLeftSection
            hasBackButton
            status={BILL_GROUP_STATUS_DISPLAY[billGroup.status]}
            title={
              <MFlex alignItems="center">
                <MText fontWeight="700" fontSize="2xl">
                  Bill Group:{' '}
                  <MText as="span" fontWeight="400" fontSize="2xl">
                    {billGroup.name}
                  </MText>
                </MText>
              </MFlex>
            }
            extraSubtitleParts={[
              <CustomerPortalConfigPopover
                key="customer-portal-config"
                accountId={billGroup.accountId}
                billGroup={billGroup}
              />,
            ]}
            id={billGroup.id}
            copyUrl
            parentLink={ROUTES.getAccountDetailsRouteWithTabName(
              billGroup.accountId,
              accountTabName,
            )}
            parentLinkTitle={account?.accountName}
            extraBadges={[
              ...(showPastDue
                ? [
                    <PastDueBadge
                      key="past-due"
                      pastDueInvoiceCount={billGroup.pastDueInvoiceCount}
                      pastDueAmount={billGroup.pastDueAmountOutstanding}
                    />,
                  ]
                : []),
              <DunningStatusBadge
                key="dunning-status"
                dunningStatus={billGroup.dunningStatus}
              />,
              <InvoiceTransformationBadge
                key="split-invoice"
                hasActiveTransformationPipeline={
                  billGroup.hasActiveTransformationPipeline
                }
                onClick={() => invoiceTransformationDrawerState.onOpen()}
              />,
            ]}
            recordAuditInfo={billGroup}
            entityJson={billGroup}
          />
        }
      >
        <MFlex flexDir="column" justifyContent="flex-end" maxW="25rem">
          <AccountDetailsBillGroupFilter
            billGroupId={billGroup.id}
            accountId={billGroup.accountId}
            onChange={(value) => {
              if (!value) {
                navigate(
                  ROUTES.getAccountDetailsRouteWithTabName(
                    billGroup.accountId,
                    accountTabName,
                  ),
                );
              } else {
                navigate(
                  ROUTES.getBillGroupPageRoute(`${value}`, billGroupTabName),
                );
              }
            }}
          />
          {billGroup?.nextInvoiceDate && (
            <BillGroupGenerateInvoicePopover
              accountId={billGroup.accountId}
              billGroupId={billGroup.id}
              billGroupStatus={billGroup.status}
              accountStatus={account.status}
              nextInvoiceDate={billGroup.nextInvoiceDate}
              errorStatus={billGroup.errorStatus}
              invoiceDelay={
                billGroup.invoiceCreation === InvoiceCreationEnum.AUTOMATIC
                  ? billGroup.invoiceDelay
                  : 0
              }
              invoiceDelayDirty={invoiceDelay !== billGroup.invoiceDelay}
            />
          )}
        </MFlex>
      </MPageHeader>
      <MBox flex="1" ml={8} minH="0" display="flex" flexDir="column">
        <MTabs
          data-testid="account-detail-tabs"
          size="sm"
          index={activeTabIndex}
          // `MCustomLinkTab`s navigate themselves — see its doc comment.
          navigate={null}
        >
          <MTabList alignItems="flex-end">
            {BILL_GROUP_TAB_ROUTES.filter(
              ({ requiresFeatureFlag }) =>
                !(
                  requiresFeatureFlag &&
                  !featureFlags[requiresFeatureFlag as keyof FeatureFlag]
                ),
            ).map(({ route, title }, index) => {
              return (
                <MCustomLinkTab to={route} key={index}>
                  {title}
                </MCustomLinkTab>
              );
            })}
          </MTabList>
        </MTabs>

        <Routes>
          <Route
            path="/"
            element={
              <BillGroupDetailsTab
                billGroup={billGroup}
                setInvoiceDelay={setInvoiceDelay}
                canEditInvoiceTransformationConfig={canEditSplitConfig}
                onOpenInvoiceTransformationDrawer={() =>
                  invoiceTransformationDrawerState.onOpen()
                }
              />
            }
          />
          <Route
            path={ROUTES.BILL_GROUP_DETAILS}
            element={
              <BillGroupDetailsTab
                billGroup={billGroup}
                setInvoiceDelay={setInvoiceDelay}
                canEditInvoiceTransformationConfig={canEditSplitConfig}
                onOpenInvoiceTransformationDrawer={() =>
                  invoiceTransformationDrawerState.onOpen()
                }
              />
            }
          />
          <Route
            path={ROUTES.BILL_GROUP_BILLING_SCHEDULE}
            element={<BillGroupBillingScheduleTab billGroup={billGroup} />}
          />
          <Route
            path={`${ROUTES.BILL_GROUP_BILLING_SCHEDULE}/${ROUTES.CONTRACT_EDIT}`}
            element={
              <PrivateRoute
                acls={[['account_quotes', 'read']]}
                component={
                  <ContractModal
                    isOpen
                    onClose={() =>
                      navigate(
                        ROUTES.getBillGroupPageRoute(
                          billGroup.id,
                          'billing-schedule',
                        ),
                      )
                    }
                  />
                }
              />
            }
          />
          {featureFlags.showSubscriptionOnAccount && (
            <Route
              path={ROUTES.BILL_GROUP_SUBSCRIPTIONS}
              element={
                <PrivateRoute
                  acls={[['billing', 'read']]}
                  component={
                    <SubscriptionList
                      account={account}
                      billGroupId={billGroupId}
                    />
                  }
                />
              }
            />
          )}
          <Route
            path={ROUTES.BILL_GROUP_USAGE}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <UsagePage billGroupId={billGroupId} account={account} />
                }
              />
            }
          />
          <Route
            path={ROUTES.BILL_GROUP_PAYMENTS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <PaymentList account={account} billGroupId={billGroupId} />
                }
              />
            }
          />

          <Route
            path={`${ROUTES.BILL_GROUP_CREDITS}/*`}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <CreditsList account={account} billGroupId={billGroupId} />
                }
              />
            }
          />

          <Route
            path={`${ROUTES.BILL_GROUP_CREDITS}/${ROUTES.CREDITS_CREATE}`}
            element={
              <PrivateRoute
                acls={[['billing', 'create']]}
                component={
                  <CreditFormCreateModal
                    isOpen
                    account={account}
                    billGroupId={billGroup.id}
                    onClose={() =>
                      navigate(
                        ROUTES.getBillGroupPageRoute(billGroup.id, 'credits'),
                      )
                    }
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.BILL_GROUP_QUOTES}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <QuoteListAccount
                    account={account}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />

          <Route
            path={`${ROUTES.BILL_GROUP_CONTRACTS}/*`}
            element={
              <PrivateRoute
                acls={[['account_quotes', 'read']]}
                component={
                  <ContractListAccount
                    account={account}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          >
            <Route
              path={ROUTES.CONTRACT_EDIT}
              element={
                <PrivateRoute
                  acls={[['account_quotes', 'read']]}
                  component={
                    <ContractModal
                      isOpen
                      onClose={() =>
                        navigate(
                          ROUTES.getBillGroupPageRoute(
                            billGroup.id,
                            'contracts',
                          ),
                        )
                      }
                    />
                  }
                />
              }
            />
          </Route>
          <Route
            path={ROUTES.BILL_GROUP_INVOICES}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <AccountInvoices
                    account={account}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />
        </Routes>
      </MBox>
      {canEditSplitConfig && invoiceTransformationDrawerState.open && (
        <InvoiceTransformationDrawer
          billGroupId={billGroup.id}
          accountId={billGroup.accountId}
          onClose={invoiceTransformationDrawerState.onClose}
        />
      )}
    </MPageContainer>
  );
};
